import React, { useState, useEffect } from 'react';
import api from '../api';
import { OrderCreate, OrderItemCreate, CustomerCreate, ShippingAddressCreate } from '../types/order';
import { formatCurrency } from '../utils/formatNumber';

interface Product {
  id: number;
  sku: string;
  name: string;
  price: number;
  inventory_available?: number;
}

interface InventoryStock {
  product_id: number;
  warehouse_id: number;
  quantity_available: number;
  quantity_on_hand: number;
  quantity_reserved: number;
}

interface Warehouse {
  id: number;
  name: string;
}

interface OrderFormProps {
  orderId?: number;
  onSave: () => void;
  onCancel: () => void;
}

interface LineItem {
  product_id: string;
  quantity: string;
  unit_price: string;
  notes: string;
  product_name?: string;
  sku?: string;
  available_quantity?: number;
}

const OrderForm: React.FC<OrderFormProps> = ({ orderId, onSave, onCancel }) => {
  // Customer information state
  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'India',
    notes: ''
  });

  // Shipping address state
  const [shippingAddress, setShippingAddress] = useState({
    recipient_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'India',
    phone: ''
  });

  // Same as billing toggle
  const [shippingSameAsBilling, setShippingSameAsBilling] = useState(false);

  // Order details state
  const [orderNotes, setOrderNotes] = useState('');
  const [warehouseId, setWarehouseId] = useState('');

  // Line items state
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { product_id: '', quantity: '1', unit_price: '', notes: '' }
  ]);

  // Reference data
  const [products, setProducts] = useState<Product[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [inventoryMap, setInventoryMap] = useState<Map<string, InventoryStock>>(new Map());

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingInventory, setLoadingInventory] = useState<Set<number>>(new Set());

  // Fetch products and warehouses on mount
  useEffect(() => {
    fetchProducts();
    fetchWarehouses();
  }, []);

  // Sync shipping address from customer data when "same as billing" is enabled
  useEffect(() => {
    if (shippingSameAsBilling) {
      setShippingAddress({
        recipient_name: customerData.name,
        address_line1: customerData.address_line1,
        address_line2: customerData.address_line2,
        city: customerData.city,
        state: customerData.state,
        postal_code: customerData.postal_code,
        country: customerData.country,
        phone: customerData.phone
      });
    }
  }, [shippingSameAsBilling, customerData]);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products/', { params: { limit: 1000 } });
      // Handle paginated response
      if (response.data && response.data.products && Array.isArray(response.data.products)) {
        setProducts(response.data.products);
      } else if (Array.isArray(response.data)) {
        // Fallback for direct array response
        setProducts(response.data);
      } else {
        console.error('Products response format unexpected:', response.data);
        setProducts([]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await api.get('/inventory/warehouses/');
      let warehouseList: Warehouse[] = [];
      if (Array.isArray(response.data)) {
        warehouseList = response.data;
      } else if (response.data && Array.isArray(response.data.warehouses)) {
        warehouseList = response.data.warehouses;
      } else {
        console.error('Warehouses response format unexpected:', response.data);
      }
      setWarehouses(warehouseList);
      // Auto-select default warehouse if none is already chosen
      if (!warehouseId && warehouseList.length > 0) {
        const defaultWh = warehouseList.find((w: any) => w.is_default) || warehouseList[0];
        if (defaultWh) setWarehouseId(String(defaultWh.id));
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      setWarehouses([]);
    }
  };

  const fetchInventory = async (productId: number, itemIndex: number) => {
    if (!warehouseId) return;

    setLoadingInventory(prev => new Set(prev).add(itemIndex));
    
    try {
      const response = await api.get('/inventory/stock/', {
        params: {
          product_id: productId,
          warehouse_id: parseInt(warehouseId)
        }
      });

      if (response.data && response.data.length > 0) {
        const stock = response.data[0];
        const key = `${productId}-${warehouseId}`;
        setInventoryMap(prev => new Map(prev).set(key, stock));

        // Update line item with available quantity
        setLineItems(prev => {
          const updated = [...prev];
          updated[itemIndex].available_quantity = stock.quantity_available;
          return updated;
        });
      } else {
        // No inventory found
        setLineItems(prev => {
          const updated = [...prev];
          updated[itemIndex].available_quantity = 0;
          return updated;
        });
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setLoadingInventory(prev => {
        const updated = new Set(prev);
        updated.delete(itemIndex);
        return updated;
      });
    }
  };

  const handleCustomerChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setCustomerData({
      ...customerData,
      [e.target.name]: e.target.value
    });
  };

  const handleShippingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShippingAddress({
      ...shippingAddress,
      [e.target.name]: e.target.value
    });
  };

  // Product typeahead state
  const [productSearches, setProductSearches] = useState<Record<number, string>>({});
  const [productDropdownOpen, setProductDropdownOpen] = useState<number | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = () => setProductDropdownOpen(null);
    if (productDropdownOpen !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [productDropdownOpen]);

  const getFilteredProducts = (index: number) => {
    const search = (productSearches[index] || '').toLowerCase();
    if (!search) return products.slice(0, 20);
    return products.filter(p =>
      p.sku.toLowerCase().includes(search) ||
      p.name.toLowerCase().includes(search)
    ).slice(0, 20);
  };

  const handleProductSearch = (index: number, value: string) => {
    setProductSearches(prev => ({ ...prev, [index]: value }));
    setProductDropdownOpen(index);
  };

  const selectProduct = (index: number, product: Product) => {
    handleLineItemChange(index, 'product_id', product.id.toString());
    setProductSearches(prev => ({ ...prev, [index]: `${product.sku} - ${product.name}` }));
    setProductDropdownOpen(null);
  };

  const handleLineItemChange = (index: number, field: keyof LineItem, value: string) => {
    const updated = [...lineItems];
    // Use type assertion since we know we're only updating string fields from form inputs
    (updated[index] as any)[field] = value;

    // If product changed, update product details and fetch inventory
    if (field === 'product_id' && value) {
      const product = products.find(p => p.id === parseInt(value));
      if (product) {
        updated[index].product_name = product.name;
        updated[index].sku = product.sku;
        updated[index].unit_price = (product.price || 0).toString();
        
        // Fetch inventory for this product
        fetchInventory(product.id, index);
      }
    }

    setLineItems(updated);
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { product_id: '', quantity: '1', unit_price: '', notes: '' }
    ]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const calculateLineTotal = (quantity: string, unitPrice: string): number => {
    const qty = parseFloat(quantity) || 0;
    const price = parseFloat(unitPrice) || 0;
    return qty * price;
  };

  const calculateOrderTotal = (): number => {
    return lineItems.reduce((total, item) => {
      return total + calculateLineTotal(item.quantity, item.unit_price);
    }, 0);
  };

  const validateForm = (): boolean => {
    // Validate customer information
    if (!customerData.name.trim()) {
      alert('Customer name is required');
      return false;
    }

    // Validate email format if provided
    if (customerData.email && !customerData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      alert('Invalid email format');
      return false;
    }

    // Validate shipping address
    if (!shippingAddress.recipient_name.trim()) {
      alert('Recipient name is required');
      return false;
    }
    if (!shippingAddress.address_line1.trim()) {
      alert('Address line 1 is required');
      return false;
    }
    if (!shippingAddress.city.trim()) {
      alert('City is required');
      return false;
    }
    if (!shippingAddress.postal_code.trim()) {
      alert('Postal code is required');
      return false;
    }
    if (!shippingAddress.country.trim()) {
      alert('Country is required');
      return false;
    }

    // Validate line items
    if (lineItems.length === 0) {
      alert('At least one line item is required');
      return false;
    }

    for (let i = 0; i < lineItems.length; i++) {
      const item = lineItems[i];
      
      if (!item.product_id) {
        alert(`Line item ${i + 1}: Product is required`);
        return false;
      }

      const quantity = parseInt(item.quantity);
      if (!quantity || quantity <= 0) {
        alert(`Line item ${i + 1}: Quantity must be a positive number`);
        return false;
      }

      const unitPrice = parseFloat(item.unit_price);
      if (unitPrice < 0) {
        alert(`Line item ${i + 1}: Unit price cannot be negative`);
        return false;
      }

      // Check inventory availability
      if (item.available_quantity !== undefined && quantity > item.available_quantity) {
        const product = products.find(p => p.id === parseInt(item.product_id));
        const proceed = window.confirm(
          `Warning: Line item ${i + 1} (${product?.name || 'Unknown'}): ` +
          `Requested quantity (${quantity}) exceeds available inventory (${item.available_quantity}). ` +
          `Do you want to proceed anyway?`
        );
        if (!proceed) {
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare customer data
      const customer: CustomerCreate = {
        name: customerData.name,
        email: customerData.email || null,
        phone: customerData.phone || null,
        billing_address: [customerData.address_line1, customerData.address_line2, customerData.city, customerData.state, customerData.postal_code, customerData.country].filter(Boolean).join(', ') || null,
        notes: customerData.notes || null
      };

      // Prepare shipping address
      const shipping: ShippingAddressCreate = {
        recipient_name: shippingAddress.recipient_name,
        address_line1: shippingAddress.address_line1,
        address_line2: shippingAddress.address_line2 || null,
        city: shippingAddress.city,
        state: shippingAddress.state || null,
        postal_code: shippingAddress.postal_code,
        country: shippingAddress.country,
        phone: shippingAddress.phone || null
      };

      // Prepare line items
      const items: OrderItemCreate[] = lineItems.map(item => ({
        product_id: parseInt(item.product_id),
        quantity: parseInt(item.quantity),
        unit_price: parseFloat(item.unit_price),
        notes: item.notes || null
      }));

      // Prepare order data
      const orderData: OrderCreate = {
        customer,
        shipping_address: shipping,
        items,
        warehouse_id: warehouseId ? parseInt(warehouseId) : null,
        notes: orderNotes || null,
        status: 'pending',
        payment_status: 'pending'
      };

      console.log('Submitting order:', orderData);

      if (orderId) {
        // Update existing order (if supported)
        await api.put(`/orders/${orderId}`, orderData);
        alert('Order updated successfully!');
      } else {
        // Create new order
        const response = await api.post('/orders/', orderData);
        console.log('Order created:', response.data);
        alert(`Order created successfully! Order Number: ${response.data.order_number}`);
      }

      onSave();
    } catch (error: any) {
      console.error('Error saving order:', error);
      console.error('Error details:', error.response?.data);

      let errorMessage = 'An unexpected error occurred';

      if (error.response?.status === 400) {
        const detail = error.response?.data?.detail;
        
        // Handle array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map((e: any) => {
            if (typeof e === 'object' && e.msg) {
              return `${e.loc?.join('.') || 'Field'}: ${e.msg}`;
            }
            return String(e);
          }).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object' && detail) {
          errorMessage = detail.msg || JSON.stringify(detail);
        } else {
          errorMessage = 'Invalid data provided. Please check your input.';
        }
      } else if (error.response?.status === 404) {
        errorMessage = 'Referenced product or warehouse not found.';
      } else if (error.response?.status === 409) {
        errorMessage = error.response?.data?.detail || 'Conflict error. Please check your data.';
      } else if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Handle different error formats
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object' && detail) {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '1000px', width: '100%' }}>
      <h3>{orderId ? 'Edit Order' : 'Create New Order'}</h3>

      <form onSubmit={handleSubmit}>
        {/* Customer / Billing Information Section */}
        <div style={{ 
          marginBottom: '2rem', 
          padding: '1.5rem', 
          border: '1px solid #e2e8f0', 
          borderRadius: '8px',
          backgroundColor: '#f8fafc'
        }}>
          <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#1e293b' }}>Customer / Billing Information</h4>
          
          <div className="form-group">
            <label className="form-label">Customer Name: *</label>
            <input
              type="text"
              name="name"
              value={customerData.name}
              onChange={handleCustomerChange}
              required
              className="form-input"
              placeholder="Enter customer name"
            />
          </div>

          <div className="form-grid-2" style={{ gap: '1rem', marginBottom: 0 }}>
            <div className="form-group">
              <label className="form-label">Email:</label>
              <input
                type="email"
                name="email"
                value={customerData.email}
                onChange={handleCustomerChange}
                className="form-input"
                placeholder="customer@example.com"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone:</label>
              <input
                type="tel"
                name="phone"
                value={customerData.phone}
                onChange={handleCustomerChange}
                className="form-input"
                placeholder="+91 98765 43210"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address Line 1: *</label>
            <input
              type="text"
              name="address_line1"
              value={customerData.address_line1}
              onChange={handleCustomerChange}
              required
              className="form-input"
              placeholder="Street address, P.O. box"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Address Line 2:</label>
            <input
              type="text"
              name="address_line2"
              value={customerData.address_line2}
              onChange={handleCustomerChange}
              className="form-input"
              placeholder="Apartment, suite, unit, building, floor, etc."
            />
          </div>

          <div className="form-grid-2" style={{ gap: '1rem', marginBottom: 0 }}>
            <div className="form-group">
              <label className="form-label">City: *</label>
              <input
                type="text"
                name="city"
                value={customerData.city}
                onChange={handleCustomerChange}
                required
                className="form-input"
                placeholder="Enter city"
              />
            </div>

            <div className="form-group">
              <label className="form-label">State/Province:</label>
              <input
                type="text"
                name="state"
                value={customerData.state}
                onChange={handleCustomerChange}
                className="form-input"
                placeholder="Enter state or province"
              />
            </div>
          </div>

          <div className="form-grid-2" style={{ gap: '1rem', marginBottom: 0 }}>
            <div className="form-group">
              <label className="form-label">Postal Code: *</label>
              <input
                type="text"
                name="postal_code"
                value={customerData.postal_code}
                onChange={handleCustomerChange}
                required
                className="form-input"
                placeholder="Enter postal code"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Country: *</label>
              <input
                type="text"
                name="country"
                value={customerData.country}
                onChange={handleCustomerChange}
                required
                className="form-input"
                placeholder="Enter country"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Customer Notes:</label>
            <textarea
              name="notes"
              value={customerData.notes}
              onChange={handleCustomerChange}
              className="form-textarea"
              rows={2}
              placeholder="Additional customer information"
            />
          </div>
        </div>

        {/* Shipping Address Section */}
        <div style={{ 
          marginBottom: '2rem', 
          padding: '1.5rem', 
          border: shippingSameAsBilling ? '2px solid #3b82f6' : '1px solid #e2e8f0', 
          borderRadius: '8px',
          backgroundColor: shippingSameAsBilling ? '#eff6ff' : '#f8fafc',
          opacity: shippingSameAsBilling ? 0.85 : 1,
          transition: 'all 0.2s ease'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0, color: '#1e293b' }}>Shipping Address</h4>
            <label style={{ 
              display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', 
              fontSize: '13px', fontWeight: 600,
              color: shippingSameAsBilling ? '#2563eb' : '#475569',
              padding: '6px 12px', borderRadius: '6px',
              background: shippingSameAsBilling ? '#dbeafe' : '#f1f5f9',
              border: shippingSameAsBilling ? '1px solid #93c5fd' : '1px solid #e2e8f0'
            }}>
              <input
                type="checkbox"
                checked={shippingSameAsBilling}
                onChange={(e) => setShippingSameAsBilling(e.target.checked)}
                style={{ accentColor: '#2563eb' }}
              />
              Same as billing address
            </label>
          </div>

          {shippingSameAsBilling && (
            <div style={{ marginBottom: '1rem', padding: '8px 12px', background: '#dbeafe', borderRadius: '6px', fontSize: '13px', color: '#1e40af' }}>
              ℹ️ Shipping address is synced from billing. Uncheck to edit separately.
            </div>
          )}
          
          <div className="form-group">
            <label className="form-label">Recipient Name: *</label>
            <input
              type="text"
              name="recipient_name"
              value={shippingAddress.recipient_name}
              onChange={handleShippingChange}
              required
              disabled={shippingSameAsBilling}
              className="form-input"
              placeholder="Enter recipient name"
              style={shippingSameAsBilling ? { backgroundColor: '#f1f5f9', cursor: 'not-allowed' } : {}}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Address Line 1: *</label>
            <input
              type="text"
              name="address_line1"
              value={shippingAddress.address_line1}
              onChange={handleShippingChange}
              required
              disabled={shippingSameAsBilling}
              className="form-input"
              placeholder="Street address, P.O. box"
              style={shippingSameAsBilling ? { backgroundColor: '#f1f5f9', cursor: 'not-allowed' } : {}}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Address Line 2:</label>
            <input
              type="text"
              name="address_line2"
              value={shippingAddress.address_line2}
              onChange={handleShippingChange}
              disabled={shippingSameAsBilling}
              className="form-input"
              placeholder="Apartment, suite, unit, building, floor, etc."
              style={shippingSameAsBilling ? { backgroundColor: '#f1f5f9', cursor: 'not-allowed' } : {}}
            />
          </div>

          <div className="form-grid-2" style={{ gap: '1rem', marginBottom: 0 }}>
            <div className="form-group">
              <label className="form-label">City: *</label>
              <input
                type="text"
                name="city"
                value={shippingAddress.city}
                onChange={handleShippingChange}
                required
                disabled={shippingSameAsBilling}
                className="form-input"
                placeholder="Enter city"
                style={shippingSameAsBilling ? { backgroundColor: '#f1f5f9', cursor: 'not-allowed' } : {}}
              />
            </div>

            <div className="form-group">
              <label className="form-label">State/Province:</label>
              <input
                type="text"
                name="state"
                value={shippingAddress.state}
                onChange={handleShippingChange}
                disabled={shippingSameAsBilling}
                className="form-input"
                placeholder="Enter state or province"
                style={shippingSameAsBilling ? { backgroundColor: '#f1f5f9', cursor: 'not-allowed' } : {}}
              />
            </div>
          </div>

          <div className="form-grid-2" style={{ gap: '1rem', marginBottom: 0 }}>
            <div className="form-group">
              <label className="form-label">Postal Code: *</label>
              <input
                type="text"
                name="postal_code"
                value={shippingAddress.postal_code}
                onChange={handleShippingChange}
                required
                disabled={shippingSameAsBilling}
                className="form-input"
                placeholder="Enter postal code"
                style={shippingSameAsBilling ? { backgroundColor: '#f1f5f9', cursor: 'not-allowed' } : {}}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Country: *</label>
              <input
                type="text"
                name="country"
                value={shippingAddress.country}
                onChange={handleShippingChange}
                required
                disabled={shippingSameAsBilling}
                className="form-input"
                placeholder="Enter country"
                style={shippingSameAsBilling ? { backgroundColor: '#f1f5f9', cursor: 'not-allowed' } : {}}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Phone:</label>
            <input
              type="tel"
              name="phone"
              value={shippingAddress.phone}
              onChange={handleShippingChange}
              disabled={shippingSameAsBilling}
              className="form-input"
              placeholder="+91 98765 43210"
              style={shippingSameAsBilling ? { backgroundColor: '#f1f5f9', cursor: 'not-allowed' } : {}}
            />
          </div>
        </div>

        {/* Order Details Section */}
        <div style={{ 
          marginBottom: '2rem', 
          padding: '1.5rem', 
          border: '1px solid #e2e8f0', 
          borderRadius: '8px',
          backgroundColor: '#f8fafc'
        }}>
          <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#1e293b' }}>Order Details</h4>
          
          <div className="form-group">
            <label className="form-label">Warehouse:</label>
            <select
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              className="form-select"
            >
              <option value="">Select Warehouse (Optional)</option>
              {warehouses.map(warehouse => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
            <small style={{ color: '#64748b', fontSize: '0.875rem' }}>
              Select a warehouse to check inventory availability
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Order Notes:</label>
            <textarea
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              className="form-textarea"
              rows={2}
              placeholder="Additional order information"
            />
          </div>
        </div>

        {/* Line Items Section */}
        <div style={{ 
          marginBottom: '2rem', 
          padding: '1.5rem', 
          border: '1px solid #e2e8f0', 
          borderRadius: '8px',
          backgroundColor: '#f8fafc'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0, color: '#1e293b' }}>Line Items</h4>
            <button
              type="button"
              onClick={addLineItem}
              className="btn btn-secondary"
              style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
            >
              + Add Item
            </button>
          </div>

          {lineItems.map((item, index) => (
            <div
              key={index}
              style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                backgroundColor: '#ffffff'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <strong style={{ color: '#475569' }}>Item {index + 1}</strong>
                {lineItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLineItem(index)}
                    className="btn btn-secondary"
                    style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.75rem',
                      backgroundColor: '#ef4444',
                      color: 'white'
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="form-group" style={{ position: 'relative' }}>
                <label className="form-label">Product: *</label>
                <input
                  type="text"
                  value={productSearches[index] ?? (item.product_id ? `${item.sku || ''} - ${item.product_name || ''}` : '')}
                  onChange={(e) => handleProductSearch(index, e.target.value)}
                  onFocus={() => setProductDropdownOpen(index)}
                  placeholder="Type to search SKU or product name..."
                  className="form-input"
                  autoComplete="off"
                />
                {productDropdownOpen === index && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'white', border: '1px solid #dde2e8', borderRadius: '4px', maxHeight: '200px', overflow: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                    {getFilteredProducts(index).length === 0 ? (
                      <div style={{ padding: '8px 12px', color: '#94a3b8', fontSize: '13px' }}>No products found</div>
                    ) : (
                      getFilteredProducts(index).map(product => (
                        <div
                          key={product.id}
                          onClick={() => selectProduct(index, product)}
                          style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#f0fdfa')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                        >
                          <span><strong>{product.sku}</strong> — {product.name} <span style={{ color: '#64748b' }}>({formatCurrency(product.price || 0)})</span></span>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: (product.inventory_available || 0) > 0 ? '#16a34a' : '#dc2626', marginLeft: '8px', whiteSpace: 'nowrap' }}>
                            {product.inventory_available ?? 0} avl
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {item.product_id && warehouseId && (
                <div style={{ 
                  padding: '0.75rem', 
                  backgroundColor: '#f1f5f9', 
                  borderRadius: '4px', 
                  marginBottom: '0.75rem',
                  fontSize: '0.875rem'
                }}>
                  {loadingInventory.has(index) ? (
                    <span style={{ color: '#64748b' }}>Loading inventory...</span>
                  ) : item.available_quantity !== undefined ? (
                    <div>
                      <strong>Available Inventory: </strong>
                      <span style={{ 
                        color: item.available_quantity > 0 ? '#16a34a' : '#dc2626',
                        fontWeight: 'bold'
                      }}>
                        {item.available_quantity} units
                      </span>
                      {parseInt(item.quantity) > item.available_quantity && (
                        <span style={{ color: '#dc2626', marginLeft: '0.5rem' }}>
                          ⚠️ Exceeds available inventory!
                        </span>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#dc2626' }}>No inventory found for this product in selected warehouse</span>
                  )}
                </div>
              )}

              <div className="form-grid-3" style={{ gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Quantity: *</label>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                    required
                    min="1"
                    className="form-input"
                    placeholder="1"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Unit Price: *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={item.unit_price}
                    onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                    required
                    min="0"
                    className="form-input"
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Line Total:</label>
                  <input
                    type="text"
                    value={formatCurrency(calculateLineTotal(item.quantity, item.unit_price))}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Notes:</label>
                <input
                  type="text"
                  value={item.notes}
                  onChange={(e) => handleLineItemChange(index, 'notes', e.target.value)}
                  className="form-input"
                  placeholder="Additional notes for this item"
                />
              </div>
            </div>
          ))}

          {/* Order Total */}
          <div style={{ 
            marginTop: '1.5rem', 
            padding: '1rem', 
            backgroundColor: '#dbeafe', 
            borderRadius: '6px',
            textAlign: 'right'
          }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1e40af' }}>
              Order Total: {formatCurrency(calculateOrderTotal())}
            </div>
            <small style={{ color: '#475569', fontSize: '0.875rem' }}>
              (Excluding tax and shipping)
            </small>
          </div>
        </div>

        {/* Form Actions */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          paddingTop: '1rem', 
          borderTop: '2px solid #e2e8f0' 
        }}>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
            style={{ minWidth: '120px' }}
          >
            {loading ? 'Saving...' : (orderId ? 'Update Order' : 'Create Order')}
          </button>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default OrderForm;
